// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewEarlierLink.test.tsx
import { ClockCounterClockwise } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { type CommentWithOptionalTags } from "./reviewCommentTags";

import { buildAskV2ChatReviewPath } from "../paths";

/**
 * The same finding in an earlier review of this artwork: "Also raised in the
 * 1 October 2026 review", linking to that review's comment, and the reviewer's
 * recorded decision when she rejected it there.
 *
 * Warhol makes the match (spec surbhi.kapoor/0061); this only draws it.
 * Rendered on the card under its header button, never inside it, for the
 * reason `JudgmentReceiptLink` gives: a link inside a button is invalid markup
 * that swallows keyboard focus. On the card rather than in the open body, so
 * a collapsed card and a grayed one in the Rejected section carry it too.
 */
export function AskV2ReviewEarlierLink({
  comment,
  commentId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
}) {
  const { t, i18n } = useTranslation("ui");
  const earlier = comment.earlierComment;
  if (!earlier) return null;
  const date = reviewDate(earlier.createdAt, i18n.language);
  const path = buildAskV2ChatReviewPath(
    earlier.chatId,
    earlier.reviewId,
    earlier.commentId,
  );
  return (
    <div
      className="px-3 pb-2"
      data-testid={`ask-v2-review-comment-earlier-${commentId}`}
    >
      <Link
        // why `as never`: the Ask v2 routes are not in the typed route tree,
        // which names this path by its v1 `$ticketId`; the rail's Past
        // reviews rows link through the same helper the same way.
        to={path.to as never}
        params={path.params as never}
        // why: a new tab, as the judgment receipt link opens one -- the
        // earlier review is another chat, and following it in place would
        // take her out of the review she is working through.
        target="_blank"
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-puntt-blue-11 underline"
        data-testid={`ask-v2-review-comment-earlier-link-${commentId}`}
        aria-label={t("askV2.review.earlierComment.openLabel")}
      >
        <ClockCounterClockwise size={14} weight="bold" aria-hidden="true" />
        {date
          ? t("askV2.review.earlierComment.raised", { date })
          : t("askV2.review.earlierComment.raisedUndated")}
      </Link>
      {earlier.disposition === "rejected" && earlier.decision ? (
        <Text
          as="p"
          size="1"
          color="gray"
          className="mt-0.5"
          data-testid={`ask-v2-review-comment-earlier-decision-${commentId}`}
        >
          {earlier.decision}
        </Text>
      ) : null}
    </div>
  );
}

/** The earlier review's day in the reader's language, or empty when the link carries no usable date. */
export function reviewDate(iso: string | undefined, language: string): string {
  if (!iso) return "";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  try {
    return when.toLocaleDateString(language || undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    // why: an unknown locale tag throws a RangeError; the day still reads
    // in the runtime's default rather than taking the link down with it.
    return when.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
}
